import { DateTime } from 'luxon';
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { crossbeamApi } from '@/api';
import {
  ATTRIBUTION_FILTER_QUERY_PARAMS,
  ATTRIBUTION_METRIC_TYPES,
  ECOSYSTEM_INSIGHTS_UNHAPPY_PATHS,
} from '@/constants/attribution';
import {
  HS3_DATA_SOURCE_TYPE,
  PIPEDRIVE_DATA_SOURCE_TYPE,
  SALESFORCE_DATA_SOURCE_TYPE,
  SNOWFLAKE_DATA_SOURCE_TYPE,
} from '@/constants/data_sources';
import { MDM_PROPERTIES } from '@/constants/mdm';
import { BASE_ROUTES } from '@/constants/routes';
import { todayYesterdayTomorrowOrCustom } from '@/date_time_utils';
import { captureException } from '@/errors';
import {
  useAttributionStore,
  usePartnersStore,
  useSourcesStore,
} from '@/stores';

import useCallouts from './useCallouts';

export default function useAttribution(props) {
  const partnersStore = usePartnersStore();
  const sourcesStore = useSourcesStore();
  const attributionStore = useAttributionStore();
  const route = useRoute();
  const router = useRouter();

  const {
    CLOSE_DATE_GTE,
    CLOSE_DATE_LTE,
    MASTER_UUID,
    OPPORTUNITY_AMOUNT_GTE,
    OPPORTUNITY_AMOUNT_LTE,
    PAGE,
  } = ATTRIBUTION_FILTER_QUERY_PARAMS;

  const hasActiveQueryParams = computed(() => {
    const queryLookup = {
      [MASTER_UUID]: true,
      [PAGE]: true,
      [CLOSE_DATE_GTE]: true,
      [CLOSE_DATE_LTE]: true,
      [OPPORTUNITY_AMOUNT_GTE]: true,
      [OPPORTUNITY_AMOUNT_LTE]: true,
    };
    return Object.keys(route.query).some((q) => queryLookup[q]);
  });

  const {
    TOTAL_SOURCED,
    TOTAL_INFLUENCED,
    TOTAL_UNATTRIBUTED,
    WIN_RATE_WITH_PARTNERS,
    WIN_RATE_WITHOUT_PARTNERS,
    DEAL_SIZE_WITH_PARTNERS,
    DEAL_SIZE_WITHOUT_PARTNERS,
    TIME_TO_CLOSE_WITH_PARTNERS,
    TIME_TO_CLOSE_WITHOUT_PARTNERS,
    TOTAL_REVENUE_WITH_PARTNERS,
    TOTAL_REVENUE_WITHOUT_PARTNERS,
    TOTAL_REVENUE_WITH_PARTNER,
    WIN_RATE_WITH_PARTNER,
    DEAL_SIZE_WITH_PARTNER,
    TIME_TO_CLOSE_WITH_PARTNER,
  } = ATTRIBUTION_METRIC_TYPES;

  const formatLookup = {
    [DEAL_SIZE_WITH_PARTNERS]: 'currency',
    [DEAL_SIZE_WITHOUT_PARTNERS]: 'currency',
    [TOTAL_SOURCED]: 'currency',
    [TOTAL_INFLUENCED]: 'currency',
    [TOTAL_UNATTRIBUTED]: 'currency',
    [TIME_TO_CLOSE_WITH_PARTNERS]: 'duration',
    [TIME_TO_CLOSE_WITHOUT_PARTNERS]: 'duration',
    [WIN_RATE_WITH_PARTNERS]: 'percentage',
    [WIN_RATE_WITHOUT_PARTNERS]: 'percentage',
    [TOTAL_REVENUE_WITH_PARTNERS]: 'currency',
    [TOTAL_REVENUE_WITHOUT_PARTNERS]: 'currency',
    [TOTAL_REVENUE_WITH_PARTNER]: 'currency',
    [WIN_RATE_WITH_PARTNER]: 'percentage',
    [DEAL_SIZE_WITH_PARTNER]: 'currency',
    [TIME_TO_CLOSE_WITH_PARTNER]: 'duration',
  };

  const settingIcon = computed(
    () =>
      ({
        [DEAL_SIZE_WITH_PARTNERS]: ['fad', 'sack-dollar'],
        [TOTAL_SOURCED]: ['fak', 'check-circle'],
        [TOTAL_INFLUENCED]: ['fak', 'partner'],
        [TOTAL_UNATTRIBUTED]: ['fak', 'exclamation-circle'],
        [TIME_TO_CLOSE_WITH_PARTNERS]: ['fak', 'calendar'],
        [WIN_RATE_WITH_PARTNERS]: ['fak', 'trophy'],
        total_attributed: ['fak', 'partner'],
        potential_revenue: ['fak', 'revenue'],
        total_overlaps: ['fak', 'mapping'],
      })[selectedMetric.value] || ['fak', 'check-circle'],
  );

  const selectedMetric = ref(props?.metric?.type);

  const settingIconColor = computed(
    () =>
      ({
        [DEAL_SIZE_WITH_PARTNERS]: 'text-success-accent',
        [TOTAL_SOURCED]: 'text-upsell-accent',
        [TOTAL_INFLUENCED]: 'text-info-accent',
        [TOTAL_UNATTRIBUTED]: 'text-warning-accent',
        [TIME_TO_CLOSE_WITH_PARTNERS]: 'text-accent-accent',
        [WIN_RATE_WITH_PARTNERS]: 'text-beta-accent',
        total_attributed: 'text-info-accent',
        potential_revenue: 'text-upsell-accent',
        total_overlaps: 'text-info-accent',
      })[selectedMetric.value] || 'text-info-accent',
  );

  const metricValueColor = computed(
    () =>
      ({
        total_revenue_with_partners: 'text-info-text ',
        win_rate_with_partners: 'text-beta-text',
        deal_size_with_partners: 'text-success-text ',
        time_to_close_with_partners: 'text-accent-text',
      })[selectedMetric.value] || 'text-info-accent',
  );

  const buildBaseAttributionItlyPayload = (deal) => {
    const result = {
      close_won_date: deal.close_date,
      opportunity_account_name: deal.record_name,
      opportunity_amount: deal.amount,
      opportunity_name: deal.name,
      opportunity_stage: deal.sales_stage,
      deal_id: deal.id.toString(),
    };
    return result;
  };

  const currencyFormatter = ({ value }) => {
    if (!value) return '$--';
    const options = {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    };
    const usd = new Intl.NumberFormat('en-US', options);
    return usd.format(value);
  };

  const dateFormatter = ({ value }) => {
    const formatter = (d) => d.toFormat('DD');
    return todayYesterdayTomorrowOrCustom(
      DateTime.fromISO(value),
      false,
      formatter,
    );
  };

  const amountFormatter = ({ value }) =>
    value?.toLocaleString('en-US', { maximumFractionDigits: 0 }) ?? '--';

  const percentFormatter = ({ value, fractionDigits = 2 }) =>
    value?.toLocaleString('en-US', {
      style: 'percent',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }) ?? '--%';

  const durationFormatter = ({ value }) =>
    `${value?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 1 }) ?? '--'} days`;

  const partnersWithPotentialRevenue = computed(() => {
    return partnersStore.overlapCounts?.overlap_usage?.by_partner?.reduce(
      (acc, curr) => {
        if (curr.open_deals_count > 0) {
          const { partner_organization_id: id } = curr;
          if (Object.hasOwn(partnersStore.partnerOrgsLookup, id))
            acc.push(partnersStore.partnerOrgsLookup[id]);
        }
        return acc;
      },
      [],
    );
  });

  const hasEnoughPotentialRevenue = computed(
    () => partnersWithPotentialRevenue.value?.length >= 3,
  );

  const { hasValidCrm, hasPartners, hasAllStandardPopulations } = useCallouts();
  const hasValidDataSource = computed(() => {
    return hasValidCrm([
      SALESFORCE_DATA_SOURCE_TYPE,
      HS3_DATA_SOURCE_TYPE,
      SNOWFLAKE_DATA_SOURCE_TYPE,
      PIPEDRIVE_DATA_SOURCE_TYPE,
    ]);
  });

  const {
    EI_MISSING_STANDARD_POPULATIONS,
    EI_NO_CRM,
    EI_UPGRADE,
    EI_NO_PARTNERS,
  } = ECOSYSTEM_INSIGHTS_UNHAPPY_PATHS;

  const isEcosystemPage = computed(
    () => route.name === BASE_ROUTES.ECOSYSTEM_INSIGHTS,
  );

  const ecosystemUnhappyPath = computed(() => {
    const possibleUnhappyPaths = [
      { condition: !hasPartners.value, copyLookup: EI_NO_PARTNERS },
      { condition: !hasValidDataSource.value, copyLookup: EI_NO_CRM },
      {
        condition: !hasAllStandardPopulations.value && isEcosystemPage.value,
        copyLookup: EI_MISSING_STANDARD_POPULATIONS,
      },
      {
        condition: !attributionStore.hasAttributionAccess,
        copyLookup: EI_UPGRADE,
      },
    ];

    return possibleUnhappyPaths.find((path) => path.condition)?.copyLookup;
  });

  const orgMissingFieldsForAttribution = computed(() =>
    Object.values(crmFieldsNotSynced.value).some(
      (missingFields) => missingFields.length > 0,
    ),
  );

  const showDataSyncEmptyState = computed(() => {
    return hasValidDataSource.value && orgMissingFieldsForAttribution.value;
  });

  const requiredMdmFields = {
    [MDM_PROPERTIES.deal.NAME]: true,
    [MDM_PROPERTIES.deal.STAGE]: true,
    [MDM_PROPERTIES.deal.AMOUNT]: true,
    [MDM_PROPERTIES.deal.CLOSE_DATE]: true,
    [MDM_PROPERTIES.deal.OPEN_DATE]: true,
    [MDM_PROPERTIES.deal.IS_WON]: true,
    [MDM_PROPERTIES.deal.IS_CLOSED]: true,
  };

  const surfaceMissingFields = (fields) => {
    return fields.reduce((acc, curr) => {
      if (requiredMdmFields[curr.mdm_property] && !curr.is_visible) {
        if (curr.mdm_property !== MDM_PROPERTIES.deal.AMOUNT)
          acc.push(curr.display_name);
      }
      return acc;
    }, []);
  };

  const crmFieldsNotSynced = computed(() => {
    const result = {
      Salesforce: [],
      HubSpot: [],
      Snowflake: [],
      Pipedrive: [],
    };
    const oppSources = sourcesStore.getSourcesByMdmType('deal');
    if (!oppSources?.length) return result;
    oppSources.forEach((source) => {
      let crmType;
      if (source.schema.includes('salesforce')) crmType = 'Salesforce';
      if (source.schema.includes('hubspot')) crmType = 'HubSpot';
      if (source.schema.includes('snowflake')) crmType = 'Snowflake';
      if (source.schema.includes('pipedrive')) crmType = 'Pipedrive';
      if (crmType) result[crmType] = surfaceMissingFields(source.fields);
    });
    return result;
  });

  const searchForAccount = async (val) => {
    const { data, error } = await crossbeamApi.GET('/v0.1/search', {
      params: {
        query: {
          search: val,
        },
      },
    });
    if (error) {
      captureException(error);
      return [];
    }
    return data.companies || [];
  };

  const pushWithExistingQuery = async ({
    query = {},
    name = '',
    params = {},
  }) => {
    const result = {};
    if (name.length) result.name = name;
    if (Object.keys(params).length) result.params = params;

    result.query = {
      ...route.query,
      ...query,
      scroll: true,
    };

    await router.push(result);
  };

  const pushWithDeletedParam = async (paramToDelete) => {
    const { [paramToDelete]: _, ...newQuery } = route.query;
    await router.push({ query: newQuery });
  };

  return {
    amountFormatter,
    orgMissingFieldsForAttribution,
    pushWithExistingQuery,
    currencyFormatter,
    dateFormatter,
    percentFormatter,
    durationFormatter,
    buildBaseAttributionItlyPayload,
    crmFieldsNotSynced,
    formatLookup,
    hasActiveQueryParams,
    hasEnoughPotentialRevenue,
    hasValidDataSource,
    partnersWithPotentialRevenue,
    pushWithDeletedParam,
    searchForAccount,
    selectedMetric,
    settingIcon,
    settingIconColor,
    showDataSyncEmptyState,
    metricValueColor,
    ecosystemUnhappyPath,
  };
}
